import { find, forOwn, includes, isArray, isEmpty, isNil, join } from 'lodash';
import { parse } from 'qs';
import btoaMethod from 'btoa';

/**
 * Checks if the given parameters are in the query string of the url and adds them if not found.
 *
 * @param {string} url - The url to check.
 * @param {object} params - The query parameters object.
 * @returns {string} The url with the query parameters.
 */
export const getURLWithQueryParams = (url, params) => {
  let urlWithParams = url;
  let queryParams = {};

  if (includes(url, '?')) queryParams = parse(url.substring(url.indexOf('?') + 1));
  else urlWithParams += '?';

  forOwn(params, (param, key) => {
    const paramRegex = new RegExp(`${key}`, 'i');
    const paramInQuery = find(queryParams, (queryParam, queryKey) => paramRegex.test(queryKey));
    if (isNil(paramInQuery)) urlWithParams += `&${key}=${isArray(param) ? join(param) : param}`;
  });

  return urlWithParams;
};

/**
 * Fetch the XML stored in the URL.
 *
 * @async
 * @param {string} url - The URL where the XML is stored.
 * @param {string} layerSubtypeSelect - Spatial Layer subtype.
 * @param {string} [connectionParams={}] - The user-password pair for the connection.
 * @returns {Promise<string>} The xml file stored in the URL as text.
 * @throws {Error} If something goes wrong.
 */
export const loadXMLFromURL = async (url, layerSubtypeSelect, connectionParams = {}) => {
  try {
    const params = { request: 'GetCapabilities', service: layerSubtypeSelect };
    const headers = {};
    if (!isEmpty(connectionParams.user) && !isEmpty(connectionParams.password)) {
      const encodedTokenAuth = btoaMethod(`${connectionParams.user}:${connectionParams.password}`);
      headers['content-type'] = 'text/plain';
      headers.Authorization = `Basic ${encodedTokenAuth}`;
    }

    const urlWithParams = getURLWithQueryParams(url, params);
    const response = await fetch(urlWithParams, {
      headers,
      mode: 'cors',
      credentials: 'omit',
      referrerPolicy: 'no-referrer'
    });
    if (response.ok) {
      const xmlText = await response.text();
      if (isNil(xmlText)) throw new Error('Empty Response');
      return xmlText;
    }
    throw new Error('Network response was not ok. Server Error');
  } catch (err) {
    throw new Error(err.message);
  }
};
